import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  getGlucosePatternInsight,
  GlucosePatternInsight,
} from '../../models/glucose-pattern-insights.generated';
import { MARKER_ROW_STYLE } from './pattern-box-styles';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowUpStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px'
};

const arrowDownStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px'
};

const conditionTitleStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'left'
};

const conditionListStyle: React.CSSProperties = {
  paddingLeft: 0,
  marginLeft: 0,
  listStyle: 'none'
};

const bulletStyle: React.CSSProperties = {
  display: 'inline-block',
  marginRight: '3px'
};

type Marker = { direction: 'up' | 'down'; label: string; severe?: boolean };

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

type PatternResolution = { categoryId: number; markers: Marker[] } | null;

export class GlucoseRegulationInsights extends React.Component<Props, {}> {

  // Whether this panel will render anything. Mirrors the guard clauses in
  // render(); kept as a static so the parent layout can drop the eval Col
  // entirely (and let the Results panel left-align) when the panel is empty.
  static shouldRender(ba: BloodworkAnalysis): boolean {
    if (!ba?.bloodwork) return false;
    const hasAnyKey = ba.glucose?.hasValue?.() || ba.hemoglobinA1c?.hasValue?.() || ba.triglyceridesHDLRatio?.hasValue?.();
    if (!hasAnyKey) return false;
    const prob = ba.glucoseAnalysis?.glucoseRegulationProbability;
    if (prob === undefined || prob < 0) return false;
    return true;
  }

  renderArrow(direction, marker, severe?: boolean) {
    const src = direction === 'up' ? arrow_up : arrow_down;
    const alt = direction === 'up' ? '↑' : '↓';
    const style = direction === 'up' ? arrowUpStyle : arrowDownStyle;
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={alt} style={style} />
        {severe && <img src={src} alt={alt} style={style} />}
        {marker}
      </span>
    );
  }

  markerIsHigh = (marker) => {
    return marker?.isGreaterThanLab?.() || marker?.isGreaterThanOptimal?.();
  };

  markerIsLow = (marker) => {
    return marker?.isLessThanLab?.() || marker?.isLessThanOptimal?.();
  };

  markerIsOptimal = (marker) => {
    return marker?.hasValue?.() && !this.markerIsHigh(marker) && !this.markerIsLow(marker);
  };

  markerHasValue = (marker) => !!marker?.hasValue?.();

  renderPatternBox = (title: string, bullets: string[], markers: Marker[], consistentWith: boolean) => {
    return (
      <div className="condition-section">
        <div style={conditionTitleStyle}>
          <div>{bullets.length > 0 && consistentWith ? `${title} Consistent with:` : title}</div>
        </div>
        {bullets.length > 0 && (
          <div style={{ textAlign: 'left' }}>
            <ul style={conditionListStyle}>
              {bullets.map((bullet) => (
                <li key={bullet} style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={bulletStyle}>•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div style={MARKER_ROW_STYLE}>
          {markers.map((marker, index) => (
            <React.Fragment key={`${marker.direction}-${marker.label}-${index}`}>
              {index > 0 && <>,{' '}</>}
              {this.renderArrow(marker.direction, marker.label, marker.severe)}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  renderPattern = (resolution: PatternResolution): React.ReactNode => {
    if (!resolution) return null;
    const insight: GlucosePatternInsight | undefined = getGlucosePatternInsight(resolution.categoryId);
    if (!insight) return null;
    return this.renderPatternBox(insight.title, insight.bullets, resolution.markers, insight.consistentWith);
  };

  // Single source of truth for the pattern boxes shown by the panel, in display
  // order. render() maps over this, and exportPatterns() reuses it so the raw
  // data export stays in sync with the UI when add-ons are added or removed.
  patternResolutions = (ba): PatternResolution[] => [
    this.computeMainPattern(ba),
    this.computeLipidAddon(ba),
    this.computeLiverAddon(ba),
    this.computeStressCortisolAddon(ba),
    this.computeStressNlrAddon(ba),
    this.computeProteinDrivenAddon(ba),
    this.computePancreaticAddon(ba),
    this.computeAmylaseAddon(ba),
    this.computeClinicallyLowGlucoseAddon(ba),
    this.computeLowGlucoseProteinAddon(ba),
    this.computeLowGlucoseCortisolAddon(ba),
    this.computeLowGlucoseB6Addon(ba),
    this.computeInsulinCpDiscrepancyAddon(ba),
    this.computeNonGlycemicTgHdlAddon(ba),
  ];

  // Serializable view of the glucose Pattern Insights for a single test, mirroring
  // exactly what the panel renders (same gating, same order, insight-less patterns
  // dropped). Used by the raw-data JSON export. Deliberately excludes supplements.
  static exportPatterns(ba: BloodworkAnalysis): Array<{
    categoryId: number;
    patternNumber: string;
    title: string;
    bullets: string[];
    consistentWith: boolean;
    markers: Marker[];
  }> {
    if (!GlucoseRegulationInsights.shouldRender(ba)) return [];
    const inst = new GlucoseRegulationInsights({ bloodworkAnalysis: ba });
    return inst
      .patternResolutions(ba)
      .filter((r): r is { categoryId: number; markers: Marker[] } => !!r)
      .map(r => ({ r, insight: getGlucosePatternInsight(r.categoryId) }))
      .filter(x => !!x.insight)
      .map(({ r, insight }) => ({
        categoryId: r.categoryId,
        patternNumber: insight!.patternNumber,
        title: insight!.title,
        bullets: insight!.bullets,
        consistentWith: insight!.consistentWith,
        markers: r.markers,
      }));
  }

  // Estimated rendered height of this panel, expressed in the layout units the
  // PDF section packer uses (bloodwork-analysis-pdf.tsx: PAGE_CAPACITY 900,
  // BASE_HEIGHT 180, ROW_HEIGHT 40 — roughly two thirds of a CSS pixel).
  //
  // The panel is a gauge block plus one bordered box per resolved pattern, and
  // each box grows with its bullet count, so its height is not fixed. Both
  // consumers — the packer (does the NEXT section still fit on the glucose
  // page?) and pdf-section.tsx's duplicate-title predictor (do the bar graphs
  // fit under the panel?) — read this one estimate so they can't drift apart.
  //
  // Calibration: GAUGE_BLOCK + one two-bullet box === 280, the value the
  // duplicate-title predictor was tuned to for the standard single-pattern
  // panel. Add-on-heavy panels correctly measure taller.
  /*
   * MEASURED off the rendered panel at its real 300px width, in the capture
   * pixels the rest of the PDF's page arithmetic uses (see pdfPanelMetrics).
   *
   * The set it replaces was hand-tuned against an older page budget and ran
   * short — 368 for a three-box panel that renders 577. That under-count is
   * what hid "Glucose Regulation & Metabolic Function" above the results when
   * they took their own page: the fit test believed they still fitted beneath
   * the gauge, so it neither forced the break nor printed the heading, and
   * html2pdf moved them anyway.
   */
  // Card header + .card-body padding + the 5px panel border, top and bottom.
  static readonly PDF_CARD_CHROME = 103;
  // The Highcharts gauge, at the height LabsmartsGuage's options declare.
  static readonly PDF_GAUGE_BLOCK_HEIGHT = 200;
  // .condition-section: its 10px margin, then 3px border and 5px padding.
  static readonly PDF_PATTERN_BOX_MARGIN = 10;
  static readonly PDF_PATTERN_BOX_CHROME = 16;
  // Bootstrap's body line-height (1.5) at the panel's 12px font.
  static readonly PDF_PATTERN_BULLET_HEIGHT = 18;
  // The 1rem Bootstrap puts under a bullet list, paid once by a box that has
  // bullets at all.
  static readonly PDF_BULLET_LIST_MARGIN = 16;
  // A box's title and its ▲/▼ row, one line each in practice.
  static readonly PDF_PATTERN_TITLE_CHARS_PER_LINE = 39;
  static readonly PDF_PATTERN_BOX_HEIGHT = 44;

  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    if (!GlucoseRegulationInsights.shouldRender(ba)) return 0;
    // exportPatterns() applies exactly the gating render() does (unresolved
    // patterns and insight-less categoryIds dropped), so the box count and
    // bullet counts here match what actually prints.
    const G = GlucoseRegulationInsights;
    const boxes = G.exportPatterns(ba).reduce((height, pattern) => {
      const titleLines = Math.max(
        1,
        Math.ceil((pattern.title || '').length / G.PDF_PATTERN_TITLE_CHARS_PER_LINE),
      );
      return (
        height +
        G.PDF_PATTERN_BOX_MARGIN +
        G.PDF_PATTERN_BOX_CHROME +
        titleLines * G.PDF_PATTERN_BULLET_HEIGHT +
        // The bullets, plus the margin under the list they sit in.
        (pattern.bullets.length
          ? G.PDF_BULLET_LIST_MARGIN +
            pattern.bullets.length * G.PDF_PATTERN_BULLET_HEIGHT
          : 0) +
        // The marker row.
        G.PDF_PATTERN_BULLET_HEIGHT
      );
    }, 0);
    return G.PDF_CARD_CHROME + G.PDF_GAUGE_BLOCK_HEIGHT + boxes;
  }

  // ─── Lipid context (used by primary pattern and 21.x) ──────────────────────
  // Mirrors the sheet's Logic column: TG/HDL high → ▲ TG/HDL; otherwise
  // ▲ Triglycerides → ▲ TG.
  getLipidMarker = (ba): Marker | null => {
    if (this.markerIsHigh(ba.triglyceridesHDLRatio)) {
      return { direction: 'up', label: 'TG/HDL' };
    }
    if (this.markerIsHigh(ba.triglycerides)) {
      return { direction: 'up', label: 'TG' };
    }
    return null;
  };

  // ─── Main pattern (groups 1–15, 19, 21) ─────────────────────────────────────
  computeMainPattern = (ba): PatternResolution => {
    const hasGlucose = this.markerHasValue(ba.glucose);
    const hasHbA1c   = this.markerHasValue(ba.hemoglobinA1c);
    const hasInsulin = this.markerHasValue(ba.insulin);
    const hasCp      = this.markerHasValue(ba.cpeptide);

    const glucoseHigh = hasGlucose && this.markerIsHigh(ba.glucose);
    const glucoseLow  = hasGlucose && this.markerIsLow(ba.glucose);
    const glucoseOpt  = hasGlucose && this.markerIsOptimal(ba.glucose);
    const hbA1cHigh   = hasHbA1c  && this.markerIsHigh(ba.hemoglobinA1c);
    const hbA1cLow    = hasHbA1c  && this.markerIsLow(ba.hemoglobinA1c);

    const insulinHigh = hasInsulin && this.markerIsHigh(ba.insulin);
    const insulinLow  = hasInsulin && this.markerIsLow(ba.insulin);
    const insulinOpt  = hasInsulin && this.markerIsOptimal(ba.insulin);
    const cpHigh      = hasCp     && this.markerIsHigh(ba.cpeptide);
    const cpLow       = hasCp     && this.markerIsLow(ba.cpeptide);
    const cpOpt       = hasCp     && this.markerIsOptimal(ba.cpeptide);

    const tgHdlHigh  = this.markerHasValue(ba.triglyceridesHDLRatio) && this.markerIsHigh(ba.triglyceridesHDLRatio);
    const tgHigh     = this.markerHasValue(ba.triglycerides) && this.markerIsHigh(ba.triglycerides);
    const lipidMarker = this.getLipidMarker(ba);

    // Discrepant insulin / C-peptide (one ↑, the other ↓) — exceedingly rare,
    // typically lab error. Both markers are treated as absent for primary-
    // pattern routing; the discrepancy is surfaced by computeInsulinCpDiscrepancyAddon.
    const mixedInsulinCpDiscrepancy =
      (insulinHigh && cpLow) || (cpHigh && insulinLow);

    const insulinCpMarkers: Marker[] = [];
    if (!mixedInsulinCpDiscrepancy) {
      if (insulinHigh) insulinCpMarkers.push({ direction: 'up', label: 'Insulin' });
      if (cpHigh)      insulinCpMarkers.push({ direction: 'up', label: 'C-peptide' });
      if (insulinLow)  insulinCpMarkers.push({ direction: 'down', label: 'Insulin' });
      if (cpLow)       insulinCpMarkers.push({ direction: 'down', label: 'C-peptide' });
    }

    // ── Primary high-glucose / high-HbA1c group (72000–72945) ──
    if (glucoseHigh || hbA1cHigh) {
      const lipidOffset = tgHdlHigh ? 50 : (tgHigh ? 100 : 0);
      const insulinVariant = (() => {
        if (mixedInsulinCpDiscrepancy) return 0;
        if (!hasInsulin && !hasCp) return 0;
        if (hasInsulin && !hasCp) {
          if (insulinHigh) return 5;
          if (insulinOpt)  return 20;
          return 35;
        }
        if (!hasInsulin && hasCp) {
          if (cpHigh) return 10;
          if (cpOpt)  return 25;
          return 40;
        }
        if (insulinHigh || cpHigh) return 15;
        if (insulinLow && cpLow)   return 45;
        if (insulinLow)            return 35; // insulin↓, cp = opt
        if (cpLow)                 return 40; // cp↓, insulin = opt
        return 30;
      })();
      if (insulinVariant < 0) return null;

      let groupBase: number;
      const baseMarkers: Marker[] = [];
      if (glucoseHigh && hbA1cHigh) {
        groupBase = 72800;
        baseMarkers.push({ direction: 'up', label: 'Glucose' });
        baseMarkers.push({ direction: 'up', label: 'HbA1c' });
      } else if (glucoseHigh && !hasHbA1c) {
        groupBase = 72000;
        baseMarkers.push({ direction: 'up', label: 'Glucose' });
      } else if (glucoseHigh) {
        groupBase = 72400;
        baseMarkers.push({ direction: 'up', label: 'Glucose' });
        if (hbA1cLow) baseMarkers.push({ direction: 'down', label: 'HbA1c' });
      } else if (hbA1cHigh && !hasGlucose) {
        groupBase = 72200;
        baseMarkers.push({ direction: 'up', label: 'HbA1c' });
      } else {
        groupBase = 72600;
        if (glucoseLow) baseMarkers.push({ direction: 'down', label: 'Glucose' });
        baseMarkers.push({ direction: 'up', label: 'HbA1c' });
      }

      const markers: Marker[] = [
        ...baseMarkers,
        ...insulinCpMarkers,
        ...(lipidMarker ? [lipidMarker] : []),
      ];

      return { categoryId: groupBase + lipidOffset + insulinVariant, markers };
    }

    // ── Low glucose primary (19.x: 73060, 73070–73083; lipid variants 73084–73097) ──
    // 73065 (▼▼ Glucose < lab range) is handled as a separate add-on so it
    // can fire alongside 73060 per the sheet's "Add-on to 19.1" rule.
    if (glucoseLow && !hbA1cHigh) {
      // configOffset selects the insulin/C-peptide variant within each
      // lipid sub-family. Same ordering across the non-lipid parents
      // (73060/70/75/80/81/82/83), the TG/HDL group (73084–73090),
      // and the TG-high group (73091–73097).
      const configOffset = (() => {
        if (!hasInsulin && !hasCp)  return 0;
        if (hasInsulin && !hasCp)   return insulinHigh ? 1 : 4;
        if (!hasInsulin && hasCp)   return cpHigh      ? 2 : 5;
        if (insulinHigh || cpHigh)  return 3;
        return 6;
      })();
      const nonLipidIds = [73060, 73070, 73075, 73080, 73081, 73082, 73083];
      const lipidBase = lipidMarker?.label === 'TG/HDL' ? 73084
                      : lipidMarker?.label === 'TG'     ? 73091
                      : null;
      const categoryId = lipidBase !== null
        ? lipidBase + configOffset
        : nonLipidIds[configOffset];
      const markers: Marker[] = [{ direction: 'down', label: 'Glucose' }, ...insulinCpMarkers];
      if (lipidBase !== null && lipidMarker) markers.push(lipidMarker);
      return { categoryId, markers };
    }

    // ── Optimal glucose + high insulin/cp (73200–73240, group 21.x) ──
    // Skip when insulin/cp are discrepant — treated as absent for routing;
    // 22.x add-on flags the discrepancy below.
    if (glucoseOpt && !hbA1cHigh && (insulinHigh || cpHigh) && !mixedInsulinCpDiscrepancy) {
      const lipidOffset21    = tgHdlHigh ? 15 : (tgHigh ? 30 : 0);
      const insulinVariant21 = (insulinHigh && cpHigh) ? 10 : (insulinHigh ? 0 : 5);
      const markers: Marker[] = [];
      if (insulinHigh) markers.push({ direction: 'up', label: 'Insulin' });
      if (cpHigh)      markers.push({ direction: 'up', label: 'C-peptide' });
      if (lipidMarker) markers.push(lipidMarker);
      return { categoryId: 73200 + lipidOffset21 + insulinVariant21, markers };
    }

    return null;
  };

  // ─── Add-on: Elevated LDL Compounding Glucose-Related Cardiometabolic Risk (16.1) ──────────
  // 73000: anyHigh + LDL high
  computeLipidAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;
    if (!this.markerIsHigh(ba.ldl)) return null;
    return {
      categoryId: 73000,
      markers: [{ direction: 'up', label: 'LDL' }],
    };
  };

  // ─── Add-on: Liver Stress May Be Contributing to Glucose Dysregulation (17.1) ───────────────
  // 73010: anyHigh + ALT high
  computeLiverAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;
    if (!this.markerIsHigh(ba.alt)) return null;
    return {
      categoryId: 73010,
      markers: [{ direction: 'up', label: 'ALT' }],
    };
  };

  // ─── Add-on: Cortisol-driven stress (18.1) ──────────────────────────────────
  computeStressCortisolAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;
    if (!this.markerIsHigh(ba.cortisolAM)) return null;
    return {
      categoryId: 73020,
      markers: [{ direction: 'up', label: 'Cortisol' }],
    };
  };

  // ─── Add-on: NLR-driven stress with optimal or absent CRP (18.2) ────────────
  computeStressNlrAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;
    if (!this.markerIsHigh(ba.nlr)) return null;
    if (this.markerIsHigh(ba.hsCRP) || this.markerIsLow(ba.hsCRP)) return null;
    return {
      categoryId: 73025,
      markers: [{ direction: 'up', label: 'NLR' }],
    };
  };

  // ─── Add-on: Protein Metabolism Influence on Glucose Dysregulation (18.3) ──
  computeProteinDrivenAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;
    if (!this.markerIsLow(ba.carbonDioxide)) return null;
    if (!this.markerIsHigh(ba.bun)) return null;
    return {
      categoryId: 73030,
      markers: [{ direction: 'down', label: 'CO2' }, { direction: 'up', label: 'BUN' }],
    };
  };

  // ─── Add-on: Limited Pancreatic Response with K/Ca (18.4–18.6) ──────────────
  // Gating per sheet G–N: anyHigh + Insulin low + C-peptide low + Potassium
  // and/or Calcium < Lab (strictly below the lab reference range). The K+Ca
  // variant takes precedence (sheet rule hides 18.4/18.5 when 18.6). Only the
  // K / Ca arrows are surfaced — Insulin / C-peptide arrows already appear in
  // the main pattern box above, so repeating them here would be noise.
  computePancreaticAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;

    const insulinLow = this.markerHasValue(ba.insulin) && this.markerIsLow(ba.insulin);
    const cpLow      = this.markerHasValue(ba.cpeptide) && this.markerIsLow(ba.cpeptide);
    if (!insulinLow || !cpLow) return null;

    const kLabLow  = !!ba.potassium?.isLessThanLab?.();
    const caLabLow = !!ba.calcium?.isLessThanLab?.();

    if (kLabLow && caLabLow) {
      return {
        categoryId: 73045,
        markers: [{ direction: 'down', label: 'K' }, { direction: 'down', label: 'Ca' }],
      };
    }
    if (kLabLow) {
      return { categoryId: 73035, markers: [{ direction: 'down', label: 'K' }] };
    }
    if (caLabLow) {
      return { categoryId: 73040, markers: [{ direction: 'down', label: 'Ca' }] };
    }
    return null;
  };

  // ─── Add-on: Low Amylase Pancreatic Context (18.7–18.10) ────────────────────
  // Gating: anyHigh + ▼ Amylase. Insulin / C-peptide low variants escalate to
  // a more specific pattern (18.10 > 18.8 / 18.9 > 18.7). Only ▼ Amylase is
  // surfaced in the marker row — Insulin / C-peptide arrows already appear in
  // the main pattern box above, so repeating them here would be noise.
  computeAmylaseAddon = (ba): PatternResolution => {
    const anyHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!anyHigh) return null;

    const amylaseLow = this.markerHasValue(ba.amylase) && this.markerIsLow(ba.amylase);
    if (!amylaseLow) return null;

    const insulinLow = this.markerHasValue(ba.insulin)  && this.markerIsLow(ba.insulin);
    const cpLow      = this.markerHasValue(ba.cpeptide) && this.markerIsLow(ba.cpeptide);
    const markers: Marker[] = [{ direction: 'down', label: 'Amylase' }];

    if      (insulinLow && cpLow) return { categoryId: 73059, markers };
    else if (insulinLow)          return { categoryId: 73055, markers };
    else if (cpLow)               return { categoryId: 73057, markers };
    else                          return { categoryId: 73050, markers };
  };

  // ─── Add-on: TG/HDL Past Midpoint, Glucose Panel Optimal ────────────────────
  // Fires on the gauge's TG/HDL-only case (see
  // GlucoseAnalysis.tgHdlPastMidpointDespiteOptimalPanel): the ratio is past the
  // lab↔optimal midpoint but Glucose, HbA1c and the insulin signal say glucose
  // handling is intact, so the practitioner is owed the differential rather than
  // a glucose reading the glucose markers contradict. Alcohol comes off the
  // client record and the thyroid signal off the panel, so the box only asks
  // about what we cannot check ourselves.
  computeNonGlycemicTgHdlAddon = (ba): PatternResolution => {
    if (!ba?.glucoseAnalysis?.tgHdlPastMidpointDespiteOptimalPanel) return null;

    const alcohol = ba.bloodwork?.client?.alcoholDrinker === true;
    // Hypothyroid direction only: low thyroid hormone slows lipid clearance and
    // raises triglycerides. A high FT4/FT3 does not.
    const thyroid =
      (this.markerHasValue(ba.tsh) && ba.tsh.isGreaterThanOptimal()) ||
      (this.markerHasValue(ba.t4Free) && ba.t4Free.isStricklyLessThanOptimal()) ||
      (this.markerHasValue(ba.t3Free) && ba.t3Free.isStricklyLessThanOptimal());

    const markers: Marker[] = [{ direction: 'up', label: 'TG/HDL' }];
    if (alcohol && thyroid) return { categoryId: 73145, markers };
    if (alcohol)            return { categoryId: 73135, markers };
    if (thyroid)            return { categoryId: 73140, markers };
    return { categoryId: 73130, markers };
  };

  // ─── Add-on: Clinically Low Glucose – Additional Evaluation (19.2) ──────────
  // Per sheet G–H: Glucose < Lab AND HbA1c blank-or-≤-optimal (i.e., not high).
  computeClinicallyLowGlucoseAddon = (ba): PatternResolution => {
    if (!this.markerHasValue(ba.glucose)) return null;
    if (!ba.glucose?.isLessThanLab?.()) return null;
    if (this.markerHasValue(ba.hemoglobinA1c) && this.markerIsHigh(ba.hemoglobinA1c)) return null;
    return {
      categoryId: 73065,
      markers: [{ direction: 'down', label: 'Glucose', severe: true }],
    };
  };

  // ─── Add-on: Low Glucose – Protein-Driven (20.1) ────────────────────────────
  computeLowGlucoseProteinAddon = (ba): PatternResolution => {
    if (!this.markerHasValue(ba.glucose)) return null;
    if (!this.markerIsLow(ba.glucose)) return null;
    if (!this.markerIsHigh(ba.bun)) return null;
    if (this.markerHasValue(ba.creatinine) && this.markerIsHigh(ba.creatinine)) return null;

    return {
      categoryId: 73100,
      markers: [{ direction: 'down', label: 'Glucose' }, { direction: 'up', label: 'BUN' }],
    };
  };

  // ─── Add-on: Low Glucose – Cortisol (20.2) ──────────────────────────────────
  computeLowGlucoseCortisolAddon = (ba): PatternResolution => {
    if (!this.markerHasValue(ba.glucose)) return null;
    if (!this.markerIsLow(ba.glucose)) return null;
    if (!this.markerIsLow(ba.cortisolAM) && !this.markerIsLow(ba.cortisolPM)) return null;

    return {
      categoryId: 73105,
      markers: [{ direction: 'down', label: 'Cortisol' }],
    };
  };

  // ─── Add-on: Insulin / C-peptide Discrepancy (22.1) ────────────────────────
  // Fires whenever Insulin and C-peptide disagree (one ↑, the other ↓). Both
  // markers were treated as absent in the primary pattern above; this add-on
  // surfaces the discrepancy so the practitioner can investigate (retest,
  // possible lab error, unusual physiology).
  computeInsulinCpDiscrepancyAddon = (ba): PatternResolution => {
    const insulinHigh = this.markerHasValue(ba.insulin)  && this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerHasValue(ba.insulin)  && this.markerIsLow(ba.insulin);
    const cpHigh      = this.markerHasValue(ba.cpeptide) && this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerHasValue(ba.cpeptide) && this.markerIsLow(ba.cpeptide);

    if (insulinHigh && cpLow) {
      return {
        categoryId: 73250,
        markers: [
          { direction: 'up',   label: 'Insulin' },
          { direction: 'down', label: 'C-peptide' },
        ],
      };
    }
    if (cpHigh && insulinLow) {
      return {
        categoryId: 73250,
        markers: [
          { direction: 'up',   label: 'C-peptide' },
          { direction: 'down', label: 'Insulin' },
        ],
      };
    }
    return null;
  };

  // ─── Add-on: Low Glucose – B6 / ALT / AST (20.3–20.6) ───────────────────────
  computeLowGlucoseB6Addon = (ba): PatternResolution => {
    if (!this.markerHasValue(ba.glucose)) return null;
    if (!this.markerIsLow(ba.glucose)) return null;

    const b6Low  = this.markerHasValue(ba.vitaminB6) && this.markerIsLow(ba.vitaminB6);
    const altLow = this.markerIsLow(ba.alt);
    const astLow = this.markerIsLow(ba.ast);

    if (b6Low) {
      return {
        categoryId: 73110,
        markers: [{ direction: 'down', label: 'B6' }],
      };
    }
    if (altLow && astLow) {
      return {
        categoryId: 73125,
        markers: [{ direction: 'down', label: 'ALT' }, { direction: 'down', label: 'AST' }],
      };
    }
    if (altLow) {
      return {
        categoryId: 73115,
        markers: [{ direction: 'down', label: 'ALT' }],
      };
    }
    if (astLow) {
      return {
        categoryId: 73120,
        markers: [{ direction: 'down', label: 'AST' }],
      };
    }
    return null;
  };

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!GlucoseRegulationInsights.shouldRender(bloodworkAnalysis)) return null;

    const ba = bloodworkAnalysis;
    const prob = bloodworkAnalysis.glucoseAnalysis?.glucoseRegulationProbability;
    const cat  = bloodworkAnalysis.glucoseAnalysis?.glucoseRegulationProbabilityCategory;

    return (
      <Card className="evaluationPanel" id="glucose-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Glucose Regulation Insights
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text="Glucose Dysregulation is"
              chartContainerId="glucoseRegulationGuage"
              bloodwork={bloodworkAnalysis.bloodwork}
              probability={prob}
              category={cat}
              ticks={[0, 50, 70, 90]}
            />

            {this.patternResolutions(ba).map((resolution, index) => (
              <React.Fragment key={index}>
                {this.renderPattern(resolution)}
              </React.Fragment>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
